import { ApolloLink, makeVar, Observable, Operation } from '@apollo/client';

interface InflightRequest {
  operation: Operation;
  callbacks: Array<() => void>;
  onComplete: (cd: () => void) => void;
}
type InflightRequestsMap = Map<string, InflightRequest[]>;

/**
 * Reactive variable that stores a map of inflight requests. This is used to track inflight requests and execute callbacks when the request is completed.
 * @public
 */
export const graphqlInflightRequests = makeVar(new Map<string, InflightRequest[]>());

/**
 * Helper function used to execute code on the resolution of a particular inflight request.
 * @public
 */
export const onInflightRequestComplete = async ({
  requestName,
  onResolved,
}: {
  requestName: string[] | string;
  onResolved: () => void;
}) => {
  const requestNames = Array.isArray(requestName) ? requestName : [requestName];
  for (const request of requestNames) {
    const inflightRequests = graphqlInflightRequests().get(request);
    if (inflightRequests) {
      const mostRecentRequest = inflightRequests[inflightRequests.length - 1];
      mostRecentRequest.onComplete(() => {
        // Recursively call this function until all inflight requests are resolved
        onInflightRequestComplete({ requestName: [request], onResolved });
      });
      return; // Exit the function if there are still inflight requests for this requestName
    }
  }

  await onResolved();
};

/**
 * Adds the operation to the inflight requests map.
 * @private
 */
const addActiveQuery = (operationName: string, operation: Operation) => {
  const currentOperations = graphqlInflightRequests().get(operationName) || [];
  const newActiveQuery: InflightRequest = {
    operation,
    callbacks: [],
    onComplete(fn: () => void) {
      this.callbacks.push(fn);
    },
  };
  graphqlInflightRequests(
    new Map(graphqlInflightRequests()).set(operationName, [...currentOperations, newActiveQuery]),
  );
};

/**
 * Removes the operation from the inflight requests map.
 * If there are no more operations for the operationName, the operationName is removed from the map.
 * @private
 */
const removeActiveQuery = (operationName: string, operation: Operation) => {
  const newActiveQueries = new Map(graphqlInflightRequests());
  const newOperations = (newActiveQueries.get(operationName) || []).filter((op) => op.operation !== operation);
  if (newOperations.length > 0) {
    newActiveQueries.set(operationName, newOperations);
  } else {
    newActiveQueries.delete(operationName);
  }
  graphqlInflightRequests(newActiveQueries);
};

/**
 * Executes all callbacks for the given operationName.
 * @private
 */
const processCallbacks = (operationName: string, operation: Operation, inflightRequests: InflightRequestsMap) => {
  inflightRequests
    .get(operationName)
    ?.find((op) => op.operation === operation)
    ?.callbacks.forEach((cb) => cb());
};

/**
 *  This link is used to track inflight requests and execute callbacks when the request is completed.
 */
export const inflightRequestsLink = new ApolloLink((operation, forward) => {
  addActiveQuery(operation.operationName, operation);
  return new Observable((observer) => {
    const subscriber = forward(operation).subscribe({
      next: (result) => {
        observer.next(result);
      },
      error: (error) => observer.error(error),
      complete: () => {
        const inflightRequests = graphqlInflightRequests();
        removeActiveQuery(operation.operationName, operation);
        processCallbacks(operation.operationName, operation, inflightRequests);
        observer.complete();
      },
    });
    return () => subscriber.unsubscribe();
  });
});
